import { PAIRED_DAY_RANGES } from './seed/holidays.js'

// Pure module: rotation rule + parties → legal-prose paragraph.
// Deterministic and framework-free so it's trivially unit-testable.
//
// Rule shapes (two tiers — see seed/holidays.js):
//   Top-tier approach:
//     { kind: 'unset' }        — no decision yet
//     { kind: 'no-change' }    — keep the base parenting schedule
//     { kind: 'suspend-flex' } — suspend base schedule; decide per year
//   Specific-arrangement (when user chose "set a specific arrangement"):
//     { kind: 'fixed', parent: 'A' | 'B', scope?: 'holiday' | 'long-weekend' }
//     { kind: 'alternating-by-year', evenParent: 'A' | 'B', scope?: 'holiday' | 'long-weekend' }
//     { kind: 'split-day', firstHalfParent: 'A' | 'B', transitionTime: 'HH:MM' }
//     { kind: 'split-period', firstHalfParent: 'A' | 'B' } — multi-day break split into halves
//     { kind: 'share', scope?: 'holiday' | 'long-weekend' }
//     { kind: 'week-on-off', startParent: 'A' | 'B' } — 1-week-on / 1-week-off rotation
//     { kind: 'paired-day-split', evenParent: 'A' | 'B', splitTime: 'HH:MM' } — 24-hour
//       Eve/Day block split at the chosen time (default 12:00), alternating yearly.
//       Available on Christmas Eve/Day and New Year's Eve/Day.
//     { kind: 'tie-to-weekend' } — long-weekend-eligible holidays only;
//       absorbs into the regular weekend parent's parenting time
//
// Holidays carry a `longWeekendType` ('always-monday' | 'always-friday' |
// 'sometimes' | null). For 'sometimes' holidays, long-weekend prose is
// gated on "if [holiday] falls on a Monday or Friday".

function nameFor(parent, parties) {
  if (parent === 'A') return parties.partyAName || 'Party A'
  if (parent === 'B') return parties.partyBName || 'Party B'
  return '[unspecified party]'
}

// "Sasha" if 1 child, "the Children" (defined-term) if 2+,
// "the children" as a generic fallback when none are provided.
function childrenSubject(parties) {
  const kids = parties?.children
  if (Array.isArray(kids) && kids.length >= 1) {
    if (kids.length === 1) return kids[0]?.name || 'the child'
    return 'the Children'
  }
  return 'the children'
}

function otherOf(parent) {
  return parent === 'A' ? 'B' : 'A'
}

function cap(s) {
  return s && s[0] ? s[0].toUpperCase() + s.slice(1) : s
}

// Convert "HH:MM" 24-hour format to readable prose ("noon", "midnight",
// "2:00 PM"). Used for paired-day-split, which embeds the transition time
// directly in the legal-prose clause.
export function formatTimeOfDay(hhmm) {
  if (typeof hhmm !== 'string') return ''
  if (hhmm === '12:00') return 'noon'
  if (hhmm === '00:00' || hhmm === '24:00') return 'midnight'
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm)
  if (!m) return hhmm
  const h = Number(m[1])
  const mins = m[2]
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return mins === '00' ? `${h12}:00 ${ampm}` : `${h12}:${mins} ${ampm}`
}

function timeWindow(pickup, dropoff, timesByAgreement) {
  if (timesByAgreement) return ' at times to be agreed by the parties'
  const p = formatTimeOfDay(pickup)
  const d = formatTimeOfDay(dropoff)
  if (p && d) return ` from ${p} to ${d}`
  if (p) return ` beginning at ${p}`
  if (d) return ` ending at ${d}`
  return ''
}

// Builds the prose pieces used in long-weekend-scoped clauses. The clause
// always opens with "For [Holiday Name]" so each entry is identifiable; for
// "sometimes" holidays a "when it falls on a Monday or Friday" qualifier is
// added inline. The body refers to "the associated long weekend" so the rule
// applies to the full Fri–Sun (or Sat–Mon) block, not just the single day.
function longWeekendPhrasing(name, longWeekendType, scope) {
  const isLongWeekend = scope === 'long-weekend'
  if (!isLongWeekend) {
    return { isLongWeekend: false, qualifier: '', blockPhrase: '' }
  }
  return {
    isLongWeekend: true,
    // qualifier always starts and ends with a comma so it composes cleanly:
    //   "For X[qualifier] the children..."
    qualifier: longWeekendType === 'sometimes' ? ', when it falls on a Monday or Friday,' : ',',
    blockPhrase: 'the associated long weekend',
  }
}

// Names we should NEVER auto-lowercase even when they appear at the start
// of customProse (so "Chris..." stays "Chris...", not "chris..."). Includes
// both party names and any supplied child names.
function properNounsFromParties(parties) {
  const out = []
  if (parties?.partyAName) out.push(parties.partyAName.trim())
  if (parties?.partyBName) out.push(parties.partyBName.trim())
  if (Array.isArray(parties?.children)) {
    for (const c of parties.children) if (c?.name) out.push(c.name.trim())
  }
  return out.filter(Boolean)
}

function startsWithProperNoun(text, parties) {
  const firstWord = (text.match(/^[\p{L}'-]+/u) || [''])[0]
  if (!firstWord) return false
  const lower = firstWord.toLowerCase()
  return properNounsFromParties(parties).some((n) => n.toLowerCase() === lower)
}

export function renderClause(holiday, parties) {
  if (holiday.customProse && holiday.customProse.trim()) {
    // Custom wording replaces the body but keeps the "For [Name], " prefix so
    // every clause in the agreement reads consistently.
    let custom = holiday.customProse.trim()
    // Lowercase the first letter so it flows naturally after the comma —
    // EXCEPT when the user opened with a party name or a child name. Those
    // are proper nouns and should keep their original capitalization.
    if (
      custom[0] && custom[0] >= 'A' && custom[0] <= 'Z' &&
      !startsWithProperNoun(custom, parties)
    ) {
      custom = custom[0].toLowerCase() + custom.slice(1)
    }
    if (!/[.!?]$/.test(custom)) custom += '.'
    return `For ${holiday.name}, ${custom}`
  }

  const { rule, name, pickup, dropoff, notes, longWeekendType, timesByAgreement } = holiday
  const window = timeWindow(pickup, dropoff, timesByAgreement)
  const A = nameFor('A', parties)
  const B = nameFor('B', parties)
  const kids = childrenSubject(parties)
  const lw = longWeekendPhrasing(name, longWeekendType, rule.scope)
  let body

  switch (rule.kind) {
    case 'no-change': {
      body = `For ${name}, the parties will keep their regular parenting-time arrangement; ${kids} will be with whichever parent's scheduled time covers that day.`
      break
    }

    case 'suspend-flex': {
      body = `For ${name}, the parties will take a flexible approach and decide together on the arrangement with reasonable notice.`
      break
    }

    case 'tie-to-weekend': {
      if (longWeekendType === 'sometimes') {
        body = `When ${name} falls on a Monday or Friday, ${kids} will stay with the parent whose scheduled parenting time covers the adjacent weekend, with that parenting time extended to include the holiday so it forms one continuous long weekend.`
      } else {
        body = `For ${name}, ${kids} will stay with the parent whose scheduled parenting time covers the adjacent weekend, with that parenting time extended to include the holiday so it forms one continuous long weekend.`
      }
      break
    }

    case 'fixed': {
      const who = nameFor(rule.parent, parties)
      if (lw.isLongWeekend) {
        body = `For ${name}${lw.qualifier} ${kids} will be with ${who} for ${lw.blockPhrase} each year${window}.`
      } else {
        body = `For ${name}, ${kids} will be with ${who} each year${window}.`
      }
      break
    }

    case 'alternating-by-year': {
      const evenName = nameFor(rule.evenParent, parties)
      const oddName = nameFor(otherOf(rule.evenParent), parties)
      if (lw.isLongWeekend) {
        body = `For ${name}${lw.qualifier} the parties will alternate ${lw.blockPhrase} each year. ${cap(kids)} will be with ${evenName} in even-numbered years and with ${oddName} in odd-numbered years${window}.`
      } else {
        body = `For ${name}, the parties will alternate each year. ${cap(kids)} will be with ${evenName} in even-numbered years and with ${oddName} in odd-numbered years${window}.`
      }
      break
    }

    case 'split-day': {
      const firstName = nameFor(rule.firstHalfParent, parties)
      const secondName = nameFor(otherOf(rule.firstHalfParent), parties)
      if (rule.transitionByAgreement) {
        body = `For ${name}, ${kids} will be with ${firstName} for the first part of the day and with ${secondName} for the second part, with the transition at a time agreed by the parties.`
      } else {
        const t = formatTimeOfDay(rule.transitionTime) || '12:00 PM'
        body = `For ${name}, ${kids} will be with ${firstName} until ${t}, and with ${secondName} from ${t} onward.`
      }
      break
    }

    case 'split-period': {
      const firstName = nameFor(rule.firstHalfParent, parties)
      const secondName = nameFor(otherOf(rule.firstHalfParent), parties)
      body = `For ${name}, ${kids} will be with ${firstName} for the first half of the break and with ${secondName} for the second half.`
      break
    }

    case 'week-on-off': {
      const startName = nameFor(rule.startParent, parties)
      const otherName = nameFor(otherOf(rule.startParent), parties)
      body = `For ${name}, the parties will rotate parenting time on a one-week-on / one-week-off basis, with ${startName} taking the first week of the break and ${otherName} the next, alternating through the duration of the break.`
      break
    }

    case 'paired-day-split': {
      const evenName = nameFor(rule.evenParent, parties)
      const oddName = nameFor(otherOf(rule.evenParent), parties)
      const range = PAIRED_DAY_RANGES[holiday.id]
      const t = formatTimeOfDay(rule.splitTime || '12:00')
      if (range) {
        body = `For ${name}, ${kids} will be with ${evenName} from ${t} on ${range.from} to ${t} on ${range.to} in even-numbered years, and with ${oddName} in odd-numbered years.`
      } else {
        // Fallback if applied to a holiday that has no paired range mapping.
        body = `For ${name}, the parties will alternate the 24-hour block annually, with ${kids} with ${evenName} in even-numbered years and ${oddName} in odd-numbered years.`
      }
      break
    }

    case 'share': {
      if (lw.isLongWeekend) {
        body = `For ${name}${lw.qualifier} both ${A} and ${B} will spend time with ${kids} together for ${lw.blockPhrase}${window}, by agreement.`
      } else {
        body = `For ${name}, both ${A} and ${B} will spend time with ${kids} together${window}, by agreement.`
      }
      break
    }

    case 'unset':
    case 'agreement-required':
    default: {
      body = `For ${name}, the parties haven't yet decided on an approach.`
      break
    }
  }

  // Capitalize the first letter when a conditional preamble was prepended.
  if (body[0] && body[0] >= 'a' && body[0] <= 'z') {
    body = body[0].toUpperCase() + body.slice(1)
  }

  if (notes && notes.trim()) {
    return `${body} ${notes.trim()}`
  }
  return body
}

// Compact one-line summary of a holiday's rule, used for the collapsed
// row chip in the editor list. Reads at a glance: "Alternates · Chris in
// even years", "Always with Gwyneth", "Eve/Day at noon · alternating",
// "Needs decision". Same parent-name + scope conventions as renderClause
// but trimmed for a chip, not a sentence.
export function summarizeRule(holiday, parties) {
  const { rule } = holiday
  const A = nameFor('A', parties)
  const B = nameFor('B', parties)
  const lwSuffix = rule && rule.scope === 'long-weekend' ? ' · long weekend' : ''

  switch (rule && rule.kind) {
    case 'no-change':
      return 'Keep regular schedule'
    case 'suspend-flex':
      return 'Flexible approach'
    case 'tie-to-weekend':
      return 'Tied to weekend parent'
    case 'fixed': {
      const who = rule.parent === 'A' ? A : B
      return `Always with ${who}${lwSuffix}`
    }
    case 'alternating-by-year': {
      const even = rule.evenParent === 'A' ? A : B
      return `Alternates · ${even} in even years${lwSuffix}`
    }
    case 'split-day': {
      const first = rule.firstHalfParent === 'A' ? A : B
      if (rule.transitionByAgreement) {
        return `Split day · ${first} first, time agreed`
      }
      const t = formatTimeOfDay(rule.transitionTime || '12:00')
      return `Split at ${t} · ${first} first`
    }
    case 'split-period': {
      const first = rule.firstHalfParent === 'A' ? A : B
      return `${first} first half, then other`
    }
    case 'share':
      return `Shared by both${lwSuffix}`
    case 'week-on-off': {
      const start = rule.startParent === 'A' ? A : B
      return `Week on/off · ${start} starts`
    }
    case 'paired-day-split': {
      const even = rule.evenParent === 'A' ? A : B
      const t = formatTimeOfDay(rule.splitTime || '12:00')
      return `Eve/Day at ${t} · ${even} in even years`
    }
    case 'unset':
    case 'agreement-required':
    default:
      return 'Needs decision'
  }
}

// Resolves which party has the children for a given holiday + year.
// Used by RotationPreview to render the "what does this look like in 2026/27/28?" table.
// Returns 'A', 'B', 'shared', 'split', 'base', 'flex', or null (unset).
export function parentForYear(rule, year) {
  switch (rule.kind) {
    case 'no-change':
      return 'base'
    case 'suspend-flex':
      return 'flex'
    case 'tie-to-weekend':
      return 'base' // resolves to whoever has the weekend in the base schedule
    case 'fixed':
      return rule.parent
    case 'alternating-by-year':
      return year % 2 === 0 ? rule.evenParent : otherOf(rule.evenParent)
    case 'split-day':
      return 'split'
    case 'split-period':
      return 'split'
    case 'week-on-off':
      return 'rotation'
    case 'paired-day-split':
      return year % 2 === 0 ? rule.evenParent : otherOf(rule.evenParent)
    case 'share':
      return 'shared'
    case 'unset':
    case 'agreement-required':
    default:
      return null
  }
}
